import * as React from 'react';
import { useParams } from 'react-router';
import { ImportProjetBudgetRequest, ProjectBudgetDTO, ProjectDto, ProjectsRycApiClient, UpdateBudgetItemDescriptionsRequest } from '../Api/ApiServer';
import { useApi } from '../Api/useApi';
import { Box, Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import 'ka-table/style.css';
import { Table } from 'ka-table';
import { DataType, EditingMode, SortingMode } from 'ka-table/enums';
import { useProject } from '../Contexts/Hooks/useProject';
import { RycIsProjectModule } from '../Enums/RycIsProjectModule';
import { useSnackbar } from 'notistack';
import QuestionDialog from '../Shared/QuestionDialog';
import { ImportBudget } from './ImportBudget';
import { ChildComponents } from 'ka-table/models';
import ProjectBudgetList from './ProjectBudgetList';
import BudgetItemDescriptionDialog from './Components/BudgetItemDescriptionDialog';

export interface IProjectBudgetProps {
}

export default function ProjectBudget(props: IProjectBudgetProps) {
    const { authorizedApi } = useApi(ProjectsRycApiClient);
    const params = useParams<{ id: string }>();
    const [projectBudget, setProjectBudget] = React.useState<ProjectBudgetDTO[]>([]);
    const getProjectBudget = React.useCallback(async () => {
        if (params?.id) {
            const budget = await authorizedApi?.projectBudget(Number(params.id));
            setProjectBudget(budget?.budgetItems ?? []);
        }
    }, [authorizedApi, params]);
    const formatNumber = (value: number, currency = false) => {
        if (value == null) return '0,00';
        return new Intl.NumberFormat('cs-CZ', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            style: currency ? 'currency' : 'decimal',
            currency: 'CZK'
        }).format(value) + (currency ? '' : '');
    };
    React.useEffect(() => {
        getProjectBudget();
    }, [getProjectBudget]);
    const { setCurrentModule, setMenuButtons } = useProject();
    React.useEffect(() => {
        setCurrentModule(RycIsProjectModule.Budget);

    }, [setCurrentModule]);
    const { enqueueSnackbar } = useSnackbar();
    const [questionOpen, setQuestionOpen] = React.useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    React.useEffect(() => {
        setMenuButtons(<Box>
            <Button variant='outlined' onClick={() => { if (projectBudget && projectBudget.length > 0) setQuestionOpen(true); else fileInputRef.current?.click(); }}>Importovat rozpočet</Button>
            <input type='file' ref={fileInputRef} style={{ display: 'none' }} onChange={(e) => {
                ImportBudget(e.target.files, async (items: ProjectBudgetDTO[]) => {
                    await authorizedApi?.importProjectBudget(new ImportProjetBudgetRequest({ projectId: Number(params.id!), budgetItems: items }));
                    enqueueSnackbar('Rozpočet importován', { variant: 'success', autoHideDuration: 2000 });
                    fileInputRef.current!.value = '';
                    getProjectBudget();
                });
            }} />
        </Box>)
    }, [setMenuButtons, params, getProjectBudget, authorizedApi, enqueueSnackbar, projectBudget]);

    const childComponents: ChildComponents = {
        groupCell: {
            content: (props: any) => {

                const sum = props.groupItems?.reduce(
                    (acc: number, item: any) => acc + (item.totalPrice || 0),
                    0
                );

                return <Box width={'100%'} display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                    <Typography variant='body2'>{props.text}</Typography>
                    <Typography fontWeight={'bold'} variant='body2'>{formatNumber(sum ?? 0) + " Kč"}</Typography>
                </Box>;
            }
        }
    };
    const theme = useTheme();
    const biggerThan700 = useMediaQuery(theme.breakpoints.up(700));
    const [selectedBudgetItem, setSelectedBudgetItem] = React.useState<ProjectBudgetDTO | null>(null);
    return (
        <Box overflow='auto' height={'calc(100vh - 144px)'} p={2}>
            <QuestionDialog open={questionOpen} onClose={(accept) => { setQuestionOpen(false); if (accept) fileInputRef.current?.click() }} title='Opravdu pokračovat?' question='Pokračováním smažete dosavadní rozpočet a nahradíte novým, opravdu chcete pokračovat?' />
            {biggerThan700 && <Table

                columns={[
                    { key: 'orderNumber', title: 'Objekt', dataType: DataType.String },
                    { key: 'orderNumberSecond', title: 'Stavební díl', dataType: DataType.String },
                    { key: 'itemCode', title: 'Kód položky', dataType: DataType.String },
                    { key: 'itemName', title: 'Název položky', dataType: DataType.String },
                    { key: 'amount', title: 'Množství', dataType: DataType.Number, style: { textAlign: 'right' } },
                    { key: 'unitPrice', title: 'Cena položky', dataType: DataType.Number, style: { textAlign: 'right' } },
                    { key: 'totalPrice', title: 'Celková cena', dataType: DataType.Number, style: { textAlign: 'right' } },
                ]}
                data={projectBudget}
                editingMode={EditingMode.None}
                rowKeyField={'id'}
                sortingMode={SortingMode.Single}
                format={({ column, value }) => {
                    if (column.key === 'amount') {
                        return formatNumber(value);
                    }
                    if (column.key === 'unitPrice') {
                        return formatNumber(value, true);
                    }
                    if (column.key === 'totalPrice') {
                        return formatNumber(value, true);
                    }
                }}
                groups={[{ columnKey: 'orderNumber' }, { columnKey: 'orderNumberSecond' }]}
                childComponents={childComponents}
            />}
            <BudgetItemDescriptionDialog
                open={selectedBudgetItem !== null}
                onClose={() => setSelectedBudgetItem(null)}
                budgetItem={selectedBudgetItem}
                onSave={(updatedItem) => {
                    authorizedApi?.updateBudgetItemDescriptions(new UpdateBudgetItemDescriptionsRequest({
                        id: updatedItem.id,
                        description: updatedItem.description,
                        contractDescription: updatedItem.contractDescription
                    })).then(() => {
                        enqueueSnackbar('Popis položky uložen', { variant: 'success', autoHideDuration: 2000 });
                        setProjectBudget(prev => prev.map(item => item.id === updatedItem.id ? updatedItem : item));

                        setSelectedBudgetItem(null);
                    })
                    // TODO: Implement save endpoint

                }}
                readOnly={false}
            />
            {!biggerThan700 && <ProjectBudgetList onItemSelected={(item) => setSelectedBudgetItem(item)} budgetItems={projectBudget} />}
        </Box>
    );
}
